// Phase 6: Admin Certificate Management Dashboard

import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from '@/components/ui/sonner';
import { 
  Award, 
  Mail, 
  FileText, 
  AlertCircle, 
  CheckCircle, 
  Clock,
  RefreshCw,
  Download
} from 'lucide-react';

interface CertificateStats {
  totalIssued: number;
  emailsSent: number;
  emailsPending: number;
  emailsFailed: number;
  pdfsGenerated: number;
  recentlyIssued: number;
}

const CertificateManagement = () => {
  const queryClient = useQueryClient();
  const [selectedCourse, setSelectedCourse] = useState<string>('all');

  // Fetch certificate statistics
  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['certificate-stats'],
    queryFn: async () => {
      const { data: certs, error } = await supabase
        .from('certificates')
        .select('*');

      if (error) throw error;

      const now = new Date();
      const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);

      const stats: CertificateStats = {
        totalIssued: certs?.length || 0,
        emailsSent: certs?.filter(c => c.email_sent).length || 0,
        emailsPending: certs?.filter(c => !c.email_sent).length || 0,
        emailsFailed: 0, // Will fetch from email queue
        pdfsGenerated: certs?.filter(c => c.pdf_url).length || 0,
        recentlyIssued: certs?.filter(c => 
          new Date(c.issue_date) >= sevenDaysAgo
        ).length || 0
      };

      return stats;
    }
  });

  // Fetch email queue status
  const { data: emailQueue } = useQuery({
    queryKey: ['email-queue-status'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('certificate_email_queue')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(20);

      if (error) throw error;
      return data;
    }
  });

  // Fetch failed certificate attempts
  const { data: failedAttempts } = useQuery({
    queryKey: ['failed-certificate-attempts'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('certificate_audit_log')
        .select('*')
        .eq('eligibility_status', 'not_eligible')
        .order('attempt_timestamp', { ascending: false })
        .limit(10);

      if (error) throw error;
      return data;
    }
  });

  // Process email queue mutation
  const processEmailQueue = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke('process-certificate-email-queue');
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      toast.success('Email queue processed successfully');
      queryClient.invalidateQueries({ queryKey: ['email-queue-status'] });
      queryClient.invalidateQueries({ queryKey: ['certificate-stats'] });
    },
    onError: (error) => {
      toast.error('Failed to process email queue: ' + error.message);
    }
  });

  // Retry failed certificate mutation
  const retryFailedCertificate = useMutation({
    mutationFn: async ({ userId, courseId }: { userId: string; courseId: string }) => {
      const { data, error } = await supabase.functions.invoke('retry-certificate-generation', {
        body: { userId, courseId }
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      toast.success('Certificate retry initiated');
      queryClient.invalidateQueries({ queryKey: ['failed-certificate-attempts'] });
    }
  });

  if (statsLoading) {
    return (
      <div className="container mx-auto py-8">
        <div className="text-center">Loading certificate data...</div>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">Certificate Management</h1>
        <p className="text-muted-foreground">
          Monitor certificate generation, email delivery, and system health
        </p>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Total Certificates</p>
              <p className="text-3xl font-bold">{stats?.totalIssued || 0}</p>
            </div>
            <Award className="h-10 w-10 text-primary" />
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            +{stats?.recentlyIssued || 0} in last 7 days
          </p>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Emails Sent</p>
              <p className="text-3xl font-bold">{stats?.emailsSent || 0}</p>
            </div>
            <Mail className="h-10 w-10 text-green-500" />
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            {stats?.emailsPending || 0} pending delivery
          </p>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">PDFs Generated</p>
              <p className="text-3xl font-bold">{stats?.pdfsGenerated || 0}</p>
            </div>
            <FileText className="h-10 w-10 text-blue-500" />
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            {((stats?.pdfsGenerated || 0) / (stats?.totalIssued || 1) * 100).toFixed(1)}% coverage
          </p>
        </Card>
      </div>

      {/* Email Queue Status */}
      <Card className="p-6 mb-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold">Email Delivery Queue</h2>
          <Button 
            onClick={() => processEmailQueue.mutate()}
            disabled={processEmailQueue.isPending}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${processEmailQueue.isPending ? 'animate-spin' : ''}`} />
            Process Queue
          </Button>
        </div>

        <div className="space-y-2">
          {emailQueue?.map(job => (
            <div key={job.id} className="flex items-center justify-between p-3 bg-muted rounded-lg">
              <div className="flex-1">
                <p className="font-medium">{job.certificate_number}</p>
                <p className="text-sm text-muted-foreground">{job.course_title}</p>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant={
                  job.status === 'sent' ? 'default' :
                  job.status === 'failed' ? 'destructive' :
                  job.status === 'processing' ? 'secondary' : 'outline'
                }>
                  {job.status === 'sent' && <CheckCircle className="h-3 w-3 mr-1" />}
                  {job.status === 'failed' && <AlertCircle className="h-3 w-3 mr-1" />}
                  {job.status === 'processing' && <Clock className="h-3 w-3 mr-1" />}
                  {job.status}
                </Badge>
                {job.retry_count > 0 && (
                  <span className="text-xs text-muted-foreground">
                    Retry {job.retry_count}/3
                  </span>
                )}
              </div>
            </div>
          ))}
          {(!emailQueue || emailQueue.length === 0) && (
            <p className="text-center text-muted-foreground py-4">
              No emails in queue
            </p>
          )}
        </div>
      </Card>

      {/* Failed Certificate Attempts */}
      <Card className="p-6">
        <h2 className="text-xl font-semibold mb-4">Recent Failed Attempts</h2>
        <div className="space-y-2">
          {failedAttempts?.map(attempt => (
            <div key={attempt.id} className="flex items-center justify-between p-3 bg-muted rounded-lg">
              <div className="flex-1">
                <p className="font-medium">{attempt.course_id}</p>
                <p className="text-sm text-muted-foreground">{attempt.rejection_reason}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Progress: {attempt.completed_modules}/{attempt.total_modules} modules
                  {attempt.quiz_average && ` • Quiz Avg: ${attempt.quiz_average}%`}
                </p>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => retryFailedCertificate.mutate({
                  userId: attempt.user_id,
                  courseId: attempt.course_id
                })}
              >
                <RefreshCw className="h-3 w-3 mr-1" />
                Retry
              </Button>
            </div>
          ))}
          {(!failedAttempts || failedAttempts.length === 0) && (
            <p className="text-center text-muted-foreground py-4">
              No failed attempts recently
            </p>
          )}
        </div>
      </Card>
    </div>
  );
};

export default CertificateManagement;
